import { c as _c } from "react/compiler-runtime";
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Tag } from '../tag';
interface TagsElasticContainerProps {
  // A record renders `key=value` pairs, for resource tags. An array renders each
  // entry as it stands and keeps duplicates, which a record cannot: two distinct
  // things can share a label, and collapsing them hides one.
  tags: Record<string, string> | string[];
  expandable?: boolean;
}
const tagValues = (tags: Record<string, string> | string[]) => Array.isArray(tags) ? tags : Object.entries(tags).map(entry => entry.filter(x => x).join('='));
export const TagsElasticContainer = t0 => {
  const $ = _c(3);
  const {
    tags,
    expandable
  } = t0;
  if (!tagValues(tags || {}).length) {
    return null;
  }
  let t1;
  if ($[0] !== expandable || $[1] !== tags) {
    t1 = <InnerTagsElasticContainer tags={tags} expandable={expandable} />;
    $[0] = expandable;
    $[1] = tags;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const InnerTagsElasticContainer = t0 => {
  const $ = _c(37);
  const {
    tags,
    expandable
  } = t0;
  const [width, setWidth] = useState(0);
  const ref = useRef(null);
  const [shownTagsCount, setShownTagsCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  let t1;
  if ($[0] !== tags) {
    t1 = tagValues(tags);
    $[0] = tags;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const values = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      let observerRefValue = null;
      const observer = new ResizeObserver(entries => {
        setWidth(entries[0].contentRect.width);
      });
      if (ref.current) {
        observer.observe(ref.current);
        observerRefValue = ref.current;
      }
      return () => {
        if (observerRefValue) {
          observer.unobserve(observerRefValue);
        }
      };
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== width) {
    t4 = () => {
      if (width === 0 || !ref.current) {
        return;
      }
      const tagsWithoutPlaceholder = getTagNodes(ref, 0);
      if (!tagsWithoutPlaceholder.length) {
        return;
      }
      let firstTagY = tagsWithoutPlaceholder[0].getBoundingClientRect().y;
      if (tagsWithoutPlaceholder.every(x => x.getBoundingClientRect().y === firstTagY)) {
        return setShownTagsCount(tagsWithoutPlaceholder.length);
      }
      const tagsWithPlaceholder = getTagNodes(ref, 1);
      if (!tagsWithPlaceholder.length) {
        return;
      }
      firstTagY = tagsWithPlaceholder[0].getBoundingClientRect().y;
      const tagsOnFirstRow = tagsWithPlaceholder.filter(x_0 => x_0.getBoundingClientRect().y === firstTagY).length - 1;
      setShownTagsCount(Math.max(tagsOnFirstRow, 1));
    };
    t5 = [ref, width];
    $[4] = width;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  const hiddenTagsCount = values.length - shownTagsCount;
  const hasOverflow = hiddenTagsCount > 0;
  let t6;
  if ($[7] !== expandable || $[8] !== expanded || $[9] !== shownTagsCount || $[10] !== values) {
    t6 = expandable && expanded ? values : values.slice(0, shownTagsCount);
    $[7] = expandable;
    $[8] = expanded;
    $[9] = shownTagsCount;
    $[10] = values;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const displayedValues = t6;
  let t7;
  let t8;
  if ($[12] !== values) {
    t7 = <TagsContainerRenderer withPlaceholder={false} values={values} />;
    t8 = <TagsContainerRenderer withPlaceholder={true} values={values} />;
    $[12] = values;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  const t9 = expandable && expanded ? "wrap" : undefined;
  let t10;
  if ($[15] !== t9) {
    t10 = {
      gap: "8px",
      alignItems: "center",
      flexWrap: t9
    };
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== displayedValues) {
    t11 = displayedValues.map(_temp);
    $[17] = displayedValues;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== expandable || $[20] !== hasOverflow || $[21] !== hiddenTagsCount) {
    t12 = hasOverflow && !expandable && <Typography variant="body2">{`+${hiddenTagsCount}`}</Typography>;
    $[19] = expandable;
    $[20] = hasOverflow;
    $[21] = hiddenTagsCount;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== expandable || $[24] !== expanded || $[25] !== hasOverflow || $[26] !== hiddenTagsCount) {
    t13 = hasOverflow && expandable && <ExpandCollapseButton expanded={expanded} hiddenCount={hiddenTagsCount} onToggle={() => setExpanded(_temp2)} />;
    $[23] = expandable;
    $[24] = expanded;
    $[25] = hasOverflow;
    $[26] = hiddenTagsCount;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t13) {
    t14 = <Stack direction="row" sx={t10}>{t11}{t12}{t13}</Stack>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14 || $[34] !== t7 || $[35] !== t8) {
    t15 = <Box className="relative" ref={ref}>{t7}{t8}{t14}</Box>;
    $[33] = t14;
    $[34] = t7;
    $[35] = t8;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
};
const ExpandCollapseButton = t0 => {
  const $ = _c(8);
  const {
    expanded,
    hiddenCount,
    onToggle
  } = t0;
  let t1;
  if ($[0] !== onToggle) {
    t1 = event => {
      event.stopPropagation();
      onToggle();
    };
    $[0] = onToggle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      borderRadius: "4px",
      padding: "3px 8px",
      border: "1px solid var(--mui-palette-divider)",
      "&:hover": {
        backgroundColor: "var(--mui-palette-primary-dark)",
        borderColor: "var(--mui-palette-primary-dark)"
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = expanded ? "Show less" : `+${hiddenCount}`;
  let t4;
  if ($[3] !== t3) {
    t4 = <Typography variant="body2">{t3}</Typography>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t4) {
    t5 = <ButtonBase onClick={t1} sx={t2}>{t4}</ButtonBase>;
    $[5] = t1;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
const getTagNodes = (rootRef: RefObject<HTMLDivElement | null>, index: number) => {
  if (!rootRef.current?.childNodes) {
    return [];
  }
  const tagsContainer = rootRef.current.childNodes.item(index) as HTMLElement;
  if (!tagsContainer?.childNodes.length) {
    return [];
  }
  return Array.from(tagsContainer.childNodes) as HTMLElement[];
};
const TagsContainerRenderer = t0 => {
  const $ = _c(8);
  const {
    values,
    withPlaceholder
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "8px",
      flexWrap: "wrap"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== withPlaceholder) {
    t2 = withPlaceholder && <ExpandCollapseButton expanded={false} hiddenCount={99} onToggle={_temp3} />;
    $[1] = withPlaceholder;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== values) {
    t3 = values.map(_temp4);
    $[3] = values;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Stack direction="row" className="absolute top-0 left-0 right-0 invisible" sx={t1}>{t2}{t3}</Stack>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
function _temp(value, index) {
  return <Tag key={`${value}-${index}`} variant="secondary" content={value} />;
}
function _temp2(prev) {
  return !prev;
}
function _temp3() {}
function _temp4(value, index) {
  return <Tag key={`${value}-${index}`} variant="secondary" content={value} />;
}